import { useRouter } from "next/router";
import { Helmet } from "react-helmet";
import styles from "./Layout.module.scss";

import useSite from "hooks/use-site";
import { helmetSettingsFromMetadata } from "lib/site";

import Nav from "components/Nav";
import Main from "components/Main";
import Footer from "components/Footer";
import { useEffect, useState } from "react";
import AcceptPdpa from "components/AcceptPdpa";
import { hasCookie, getCookie } from "cookies-next";

// Memorial cookie functions
const getMemorialCookie = () => {
  if (typeof window === 'undefined') return null;
  const cookieName = 'have_seen_memorial';
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === cookieName) {
      // Parse JSON value to get language and status
      try {
        return JSON.parse(decodeURIComponent(value));
      } catch {
        return null;
      }
    }
  }
  return null;
};

const Layout = ({
    children,
    isPreview = false,
    isHomePage = false,
    isFullColor = false,
    isSplash = false,
    templateType = "default",
}) => {
    const router = useRouter();
    const { asPath } = router;

    const { homepage, metadata = {} } = useSite();

    const [acceptPDPA, setAcceptPDPA] = useState(
        hasCookie(process.env.COOKIE_PDPA)
            ? getCookie(process.env.COOKIE_PDPA)
            : false,
    );
    const [loading, setLoading] = useState(true);

    if (!metadata?.og) {
        metadata.og = {};
    }

    metadata.og.url = `${homepage}${asPath}`;

    const helmetSettings = {
        defaultTitle: metadata.title,
        titleTemplate:
            process.env.WORDPRESS_PLUGIN_SEO === true
                ? "%s"
                : `%s - ${metadata.title}`,
        ...helmetSettingsFromMetadata(metadata, {
            setTitle: false,
            link: [
                {
                    rel: "alternate",
                    type: "application/rss+xml",
                    href: "/feed.xml",
                },

                // Favicon sizes and manifest generated via https://favicon.io/

                {
                    rel: "apple-touch-icon",
                    sizes: "180x180",
                    href: "/jts-ico.ico",
                },
                {
                    rel: "icon",
                    type: "image/png",
                    sizes: "16x16",
                    href: "/jts-ico.ico",
                },
                {
                    rel: "icon",
                    type: "image/png",
                    sizes: "32x32",
                    href: "/jts-ico.ico",
                },
                {
                    rel: "manifest",
                    href: "/site.webmanifest",
                },
            ],
        }),
    };

    useEffect(() => {
        // Check memorial cookie - redirect to memorial page if not seen
        
        if (process.env.MEMORIAL_ENABLE) {
            const checkMemorialCookie = () => {
                const memorialData = getMemorialCookie();
                // If user hasn't seen memorial, redirect to memorial page
                if (!memorialData || memorialData.seen !== true) {
                    const memorialUrl = '/memorial';
                    if (typeof window !== 'undefined') {
                        window.location.href = memorialUrl;
                    }
                }
            };
            checkMemorialCookie();
        }

        if (process.env.APP_ENV !== "development") {
            document.addEventListener("contextmenu", e => {
                e.preventDefault();
            });
        }
    }, []);

    useEffect(() => {
        setLoading(false);
    }, [acceptPDPA]);

    const handlerCopy = e => {
        if (process.env.APP_ENV !== "development") {
            e.preventDefault();
            return false;
        }
    };

    const handlerCut = e => {
        if (process.env.APP_ENV !== "development") {
            e.preventDefault();
            return false;
        }
    };

    return !isSplash ? (
        <div
            className={styles.layoutContainer}
            onCopy={handlerCopy}
            onCut={handlerCut}>
            <Helmet {...helmetSettings} />

            <Nav
                isHomePage={isHomePage}
                isFullColor={isFullColor}
                isPreview={isPreview}
                templateType={templateType}
            />

            <Main>{children}</Main>
            {!acceptPDPA && !loading && <AcceptPdpa />}
            <Footer />
        </div>
    ) : (
        <div onCopy={handlerCopy} onCut={handlerCut}>
            <Helmet {...helmetSettings} />
            <div>{children}</div>
        </div>
    );
};

export default Layout;
